<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { onMounted, ref } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useScrollAnimation, usePrefersReducedMotion } from '@/home/hooks/animation';
import Banner from './components/Banner.vue'
import Projects from './components/Projects.vue';
import CaseStudySection from './components/CaseStudySection.vue';
import Information from './components/Information.vue'
import Services from './components/Services.vue';
import Events from './components/Events.vue';
import Journalism from './components/Journalism.vue';
import Assistant from './components/Assistant.vue';
import Contributors from './components/Contributors.vue';
import Calendar from './components/Calendar.vue';

const { locale } = useI18n();
const { observe } = useScrollAnimation({ threshold: 0.1, once: true });
const prefersReducedMotion = usePrefersReducedMotion();

const animatedElements = ref<HTMLElement[]>([]);

const collectAnimatedElements = (el: Element | ComponentPublicInstance | null) => {
  if (el && el instanceof HTMLElement && !animatedElements.value.includes(el)) {
    animatedElements.value.push(el);
  }
};

onMounted(() => {
  if (prefersReducedMotion.value) { return; }

  animatedElements.value.forEach((el) => {
    observe(el);
  });
});
</script>

<template>
  <main class="page-home overflow-hidden bg-white">
    <div class="hero-container">
      <Banner />
    </div>

    <div
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Projects />
    </div>

    <div
      v-if="locale === 'zh-CN'"
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <CaseStudySection />
    </div>

    <div
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Information />
    </div>

    <div
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Services />
    </div>

    <Assistant v-if="locale === 'zh-CN'" />

    <div
      v-if="locale === 'zh-CN'"
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Events />
    </div>

    <div
      v-if="locale === 'zh-CN'"
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Calendar />
    </div>

    <div
      v-if="locale === 'zh-CN'"
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Journalism />
    </div>

    <div
      :ref="collectAnimatedElements"
      class="animate-on-scroll"
    >
      <Contributors />
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-home {
  * {
    box-sizing: border-box;
  }
}

.hero-container {
  position: relative;
  overflow: hidden;
}

.animate-on-scroll {
  transform: translateY(30px);
  opacity: 0;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;
}

.animate-on-scroll.animated {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .animate-on-scroll {
    transform: none;
    opacity: 1;
    transition: none;
  }
}
</style>
